(function () {
  'use strict';

  if (window.BCPassportHeartInitialized) return;
  window.BCPassportHeartInitialized = true;

  const CFG = window.BC_LISTS || {};
  const PROXY = CFG.proxyUrl || '/apps/passport';
  let serverSavedProductIds = null;
  let serverSavedProductIdsPromise = null;
  let heartMutationObserver = null;

  function redirectToLogin() {
    const ret = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/account/login?return_url=${ret}`;
  }

  function heartSVG(saved, size) {
    const fill = saved ? 'currentColor' : 'none';
    return `
      <svg class="bc-icon bc-icon--heart" width="${size}" height="${size}" viewBox="0 0 24 24" fill="${fill}" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" aria-hidden="true" focusable="false">
        <use href="#bc-icon-heart"></use>
      </svg>
    `;
  }

  function heartMarkup(btn, saved) {
    const svg = heartSVG(saved, 20);

    if (btn.classList.contains('quick-add__button--passport')) {
      return `
        <span class="add-to-cart-text quick-add__button-content">
          <span aria-hidden="true" class="svg-wrapper add-to-cart-icon quick-add__button-icon">
            ${svg}
          </span>
        </span>
      `;
    }

    return svg;
  }

  async function apiFetch(path, opts = {}) {
    const res = await fetch(PROXY + path, {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      ...opts,
    });

    if (!res.ok) {
      const body = await res.text().catch(() => '');
      const error = new Error(`${path} -> ${res.status}: ${body}`);
      error.status = res.status;
      error.body = body;
      throw error;
    }

    return res.json().catch((parseError) => {
      const error = new Error(`${path} -> malformed response: ${parseError.message}`);
      error.status = res.status;
      throw error;
    });
  }

  const api = {
    get: (path) => apiFetch(path),
    post: (path, body) => apiFetch(path, { method: 'POST', body: JSON.stringify(body) }),
  };

  function isProductSaved(productId) {
    try {
      return JSON.parse(localStorage.getItem('bc_saved') || '[]').includes(String(productId));
    } catch {
      return false;
    }
  }

  function localSave(productId, save) {
    try {
      const current = JSON.parse(localStorage.getItem('bc_saved') || '[]');
      const next = save
        ? [...new Set([...current, String(productId)])]
        : current.filter((id) => id !== String(productId));
      localStorage.setItem('bc_saved', JSON.stringify(next));
    } catch {
      // no-op
    }
  }

  function listCanDriveHeartState(list) {
    if (!list) return false;
    return !list.isLocked && list.kind !== 'generated_purchase_history';
  }

  function itemProductId(item) {
    return item && (item.productId || item.id || item.shopifyProductId);
  }

  function listHasProduct(list, productId) {
    const items = Array.isArray(list?.items) ? list.items : [];
    return items.some((item) => String(itemProductId(item)) === String(productId));
  }

  function listLooksDefault(list) {
    const id = String(list.id || '').toLowerCase();
    const slug = String(list.slug || '').toLowerCase();
    return Boolean(list.isDefault || id === 'default' || slug === 'default');
  }

  function savedProductIdsFromListsResponse(response) {
    const ids = new Set();
    const lists = Array.isArray(response?.lists) ? response.lists.filter(listCanDriveHeartState) : [];
    const sourceLists = lists;

    sourceLists.forEach((list) => {
      const items = Array.isArray(list.items) ? list.items : [];
      items.forEach((item) => {
        const productId = itemProductId(item);
        if (productId) ids.add(String(productId));
      });
    });

    return ids;
  }

  async function getServerSavedProductIds() {
    if (!CFG.customerId) return null;
    if (serverSavedProductIds) return serverSavedProductIds;

    if (!serverSavedProductIdsPromise) {
      serverSavedProductIdsPromise = api.get('/customer/lists')
        .then((response) => {
          serverSavedProductIds = savedProductIdsFromListsResponse(response);
          return serverSavedProductIds;
        })
        .catch((error) => {
          serverSavedProductIdsPromise = null;
          throw error;
        });
    }

    return serverSavedProductIdsPromise;
  }

  function rememberServerProductState(productId, saved) {
    if (!serverSavedProductIds) return;
    if (saved) {
      serverSavedProductIds.add(String(productId));
    } else {
      serverSavedProductIds.delete(String(productId));
    }
  }

  async function syncHeartStateFromServer(btn, productId) {
    if (!CFG.customerId) return;

    try {
      const ids = await getServerSavedProductIds();
      if (!ids || btn.dataset.bcHeartUserTouched === 'true') return;

      const saved = ids.has(String(productId));
      localSave(productId, saved);
      setHeartState(btn, saved);
    } catch (error) {
      console.warn('Passport heart state sync failed', error);
    }
  }

  async function syncHeartButtonsFromServer(buttons) {
    if (!CFG.customerId || !buttons.length) return;

    try {
      const ids = await getServerSavedProductIds();
      if (!ids) return;

      try {
        localStorage.setItem('bc_saved', JSON.stringify(Array.from(ids)));
      } catch {
        // Local state is only a cache; server state remains authoritative.
      }

      requestAnimationFrame(() => {
        for (const { btn, productId } of buttons) {
          if (!btn.isConnected || btn.dataset.bcHeartUserTouched === 'true') continue;
          setHeartState(btn, ids.has(String(productId)));
        }
      });
    } catch (error) {
      console.warn('Passport heart state sync failed', error);
    }
  }

  function setHeartState(btn, saved) {
    btn.innerHTML = heartMarkup(btn, saved);
    btn.classList.toggle('bc-heart-btn--active', saved);
    btn.setAttribute('aria-pressed', String(saved));
    const savedLabel = isQuickCardHeartButton(btn) ? 'Saved to your list' : 'Remove from your list';
    btn.setAttribute('aria-label', saved ? savedLabel : 'Save to your list');
    btn.title = saved ? 'Saved' : 'Save to your list';
  }

  function isQuickCardHeartButton(btn) {
    return btn.classList.contains('quick-add__button--passport');
  }

  function isQuickCardWishlistEnabled(btn) {
    if (!isQuickCardHeartButton(btn)) return true;

    const card = btn.closest('product-card');
    return Boolean(card && card.dataset.quickAddWishlistEnabled === 'true');
  }

  function isPassportAppHeartButton(btn) {
    if (btn.closest('[data-bc-passport-heart-slot]')) return true;

    const wrap = btn.closest('[data-bc-heart-wrap]');
    return Boolean(wrap && !btn.classList.contains('quick-add__button--passport'));
  }

  function findCardPassportAppButton(btn) {
    const card = btn.closest('product-card');
    if (!card) return null;

    const productId = btn.dataset.productId;
    const appButtons = Array.from(card.querySelectorAll('[data-bc-heart-wrap]'))
      .map((wrap) => {
        if (!(wrap instanceof HTMLElement)) return null;
        if (wrap === btn || wrap.contains(btn)) return null;

        const button =
          wrap.matches('button, a, [role="button"], .bc-heart-btn')
            ? wrap
            : wrap.querySelector('.bc-heart-btn, button, a, [role="button"]');
        if (!(button instanceof HTMLElement)) return null;
        if (button.classList.contains('quick-add__button--passport')) return null;

        const buttonProductId = button.dataset.productId || wrap.dataset.productId;
        if (productId && buttonProductId && String(buttonProductId) !== String(productId)) return null;

        return button;
      })
      .filter(Boolean);

    return appButtons[0] || null;
  }

  function clickCardPassportAppButton(btn) {
    const appButton = findCardPassportAppButton(btn);
    if (!appButton) return false;

    appButton.click();
    return true;
  }

  function animateQuickCardHeart(btn) {
    btn.classList.remove('bc-heart-btn--clicked');
    void btn.offsetWidth;
    btn.classList.add('bc-heart-btn--clicked');

    window.setTimeout(() => {
      btn.classList.remove('bc-heart-btn--clicked');
    }, 420);
  }

  function findAll(root, selector) {
    const matches = [];
    if (root instanceof Element && root.matches(selector)) {
      matches.push(root);
    }
    root.querySelectorAll?.(selector).forEach((element) => matches.push(element));
    return matches;
  }

  function heartMountScope(element) {
    if (!(element instanceof Element)) return document;
    return (
      element.closest('.product-details') ||
      element.closest('product-card') ||
      element.closest('.shopify-section') ||
      document
    );
  }

  function mountPassportAppHearts(root = document) {
    const slots = findAll(root, '[data-bc-passport-heart-slot]');
    const wrapsByScope = new Map();

    slots.forEach((slot) => {
      if (!(slot instanceof HTMLElement)) return;
      if (slot.dataset.bcPassportMounted === 'true') return;

      const productId = slot.dataset.productId;
      const scope = heartMountScope(slot);
      let appWraps = wrapsByScope.get(scope);
      if (!appWraps) {
        appWraps = findAll(scope, '[data-bc-heart-wrap]');
        wrapsByScope.set(scope, appWraps);
      }

      const appWrap = appWraps.find((wrap) => {
        if (!(wrap instanceof HTMLElement)) return false;
        if (wrap.closest('[data-bc-passport-heart-slot]')) return false;
        if (wrap.classList.contains('quick-add__button--passport')) return false;

        const button = wrap instanceof HTMLButtonElement ? wrap : wrap.querySelector('.bc-heart-btn, button');
        if (!(button instanceof HTMLButtonElement)) return false;
        if (button.classList.contains('quick-add__button--passport')) return false;
        if (!productId) return true;

        const buttonProductId = button.dataset.productId || wrap.dataset.productId;
        return !buttonProductId || String(buttonProductId) === String(productId);
      });

      if (!appWrap) return;

      slot.appendChild(appWrap);
      slot.dataset.bcPassportMounted = 'true';
    });
  }

  function handleSaveLabelClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const label = target.closest('[data-bc-savelabel]');
    if (!label) return;

    const item = label.closest('.bc-cartnote__item--save, .bc-cartnote__save');
    const heart = item && item.querySelector('.bc-heart-btn');
    if (heart instanceof HTMLElement) {
      event.preventDefault();
      heart.click();
    }
  }

  function ensureHeartToastStyles() {
    if (document.querySelector('[data-bc-heart-toast-styles]')) return;

    const style = document.createElement('style');
    style.dataset.bcHeartToastStyles = 'true';
    style.textContent = `
      .bc-heart-toast {
        position: fixed;
        inset-block-end: 2rem;
        inset-inline-end: 5.75rem;
        z-index: 2147483000;
        width: 220px;
        max-width: calc(100vw - 2rem);
        padding: var(--padding-sm, 1rem) var(--padding-md, 1.6rem);
        border-radius: 6px;
        background: #2f6b4f;
        color: #ffffff;
        font-family: var(--font-paragraph--family);
        font-style: var(--font-paragraph--style);
        font-size: var(--font-size--sm, 1.4rem);
        font-weight: 600;
        line-height: 1.3;
        text-align: center;
        opacity: 0;
        pointer-events: none;
        transform: translateY(0.5rem);
        transition: opacity 220ms ease, transform 220ms ease;
      }

      .bc-heart-toast--visible {
        opacity: 1;
        transform: translateY(0);
      }

      .bc-heart-toast--hiding {
        opacity: 0;
        transform: translateY(0.5rem);
      }

      .bc-heart-toast--removed,
      .bc-heart-toast--error {
        background: #343434;
      }

      @media screen and (max-width: 749px) {
        .bc-heart-toast {
          inset-inline-start: 50%;
          inset-inline-end: auto;
          inset-block-end: 1rem;
          width: fit-content;
          transform: translate(-50%, 0.5rem);
        }

        .bc-heart-toast--visible {
          transform: translate(-50%, 0);
        }

        .bc-heart-toast--hiding {
          transform: translate(-50%, 0.5rem);
        }
      }
    `;
    document.head.appendChild(style);
  }

  function ensureQuickHeartAnimationStyles() {
    if (document.querySelector('[data-bc-quick-heart-animation-styles]')) return;

    const style = document.createElement('style');
    style.dataset.bcQuickHeartAnimationStyles = 'true';
    style.textContent = `
      .quick-add__button--passport.bc-heart-btn--clicked svg {
        animation: bc-quick-heart-click-bounce 360ms ease;
      }

      @keyframes bc-quick-heart-click-bounce {
        0% {
          transform: scale(1);
        }

        35% {
          transform: scale(0.82);
        }

        70% {
          transform: scale(1.16);
        }

        100% {
          transform: scale(1);
        }
      }
    `;
    document.head.appendChild(style);
  }

  function showHeartToast(btn, message, type = 'saved') {
    ensureHeartToastStyles();

    let toast = document.querySelector('[data-bc-heart-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'bc-heart-toast';
      toast.dataset.bcHeartToast = 'true';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.hidden = true;
      document.body.appendChild(toast);
    }

    toast.textContent = message;
    toast.hidden = false;
    toast.classList.remove(
      'bc-heart-toast--visible',
      'bc-heart-toast--hiding',
      'bc-heart-toast--removed',
      'bc-heart-toast--error'
    );
    if (type === 'removed') {
      toast.classList.add('bc-heart-toast--removed');
    } else if (type === 'error') {
      toast.classList.add('bc-heart-toast--error');
    }
    clearTimeout(toast.bcHeartToastTimer);
    clearTimeout(toast.bcHeartToastHideTimer);

    requestAnimationFrame(() => {
      toast.classList.add('bc-heart-toast--visible');
    });

    toast.bcHeartToastTimer = setTimeout(() => {
      toast.classList.remove('bc-heart-toast--visible');
      toast.classList.add('bc-heart-toast--hiding');
      toast.bcHeartToastHideTimer = setTimeout(() => {
        toast.classList.remove('bc-heart-toast--hiding');
        toast.hidden = true;
      }, 220);
    }, 2000);
  }

  function listLabel(list) {
    return list?.name || list?.title || list?.label || (listLooksDefault(list) ? 'My list' : list?.slug) || 'List';
  }

  function listIdentifier(list) {
    return list?.id || list?.slug || (listLooksDefault(list) ? 'default' : '');
  }

  function listItemEndpoints(list) {
    if (listLooksDefault(list)) return ['/customer/lists/default/items'];

    const identifiers = [list?.id, list?.slug, listIdentifier(list)].filter(Boolean);
    return [...new Set(identifiers.map((identifier) => `/customer/lists/${encodeURIComponent(identifier)}/items`))];
  }

  async function saveProductToList(btn, productId, list) {
    if (!CFG.customerId) {
      redirectToLogin();
      return false;
    }

    const endpoints = listItemEndpoints(list);
    if (!endpoints.length) throw new Error('Passport list is missing an identifier.');

    const payload = {
      productId: String(productId),
      variantId: btn.dataset.variantId || '',
      handle: btn.dataset.handle || '',
    };

    let lastError = null;
    for (const endpoint of endpoints) {
      try {
        await api.post(endpoint, payload);
        lastError = null;
        break;
      } catch (error) {
        lastError = error;
      }
    }

    if (lastError) throw lastError;

    localSave(productId, true);
    rememberServerProductState(productId, true);
    setHeartState(btn, true);
    return true;
  }

  function listItemDeleteEndpoints(list, productId) {
    if (listLooksDefault(list)) {
      return [`/customer/lists/default/items/${encodeURIComponent(productId)}/delete`];
    }

    const identifiers = [list?.id, list?.slug, listIdentifier(list)].filter(Boolean);
    return [
      ...new Set(
        identifiers.map(
          (identifier) => `/customer/lists/${encodeURIComponent(identifier)}/items/${encodeURIComponent(productId)}/delete`
        )
      ),
    ];
  }

  async function removeProductFromAllLists(btn, productId) {
    localSave(productId, false);

    try {
      if (!CFG.customerId) {
        setHeartState(btn, false);
        rememberServerProductState(productId, false);
        return true;
      }

      const lists = await getPassportLists();
      const targetLists = lists.filter((list) => listHasProduct(list, productId));
      const listsToUpdate = targetLists.length ? targetLists : [{ isDefault: true, id: 'default' }];

      for (const list of listsToUpdate) {
        const endpoints = listItemDeleteEndpoints(list, productId);
        let removed = false;
        let lastError = null;

        for (const endpoint of endpoints) {
          try {
            await api.post(endpoint, {});
            removed = true;
            break;
          } catch (error) {
            lastError = error;
          }
        }

        if (!removed && lastError) throw lastError;
      }

      setHeartState(btn, false);
      rememberServerProductState(productId, false);
      return true;
    } catch (error) {
      localSave(productId, true);
      setHeartState(btn, true);
      if (error.status === 401) {
        redirectToLogin();
        return false;
      }
      throw error;
    }
  }

  async function createPassportList(name) {
    const trimmedName = String(name || '').trim();
    if (!trimmedName) throw new Error('List name is required.');

    try {
      return await api.post('/customer/lists', { name: trimmedName });
    } catch (error) {
      if (error.status && ![400, 404, 422].includes(error.status)) throw error;
      return api.post('/customer/lists', { title: trimmedName });
    }
  }

  async function getPassportLists() {
    const response = await api.get('/customer/lists');
    return Array.isArray(response?.lists) ? response.lists.filter(listCanDriveHeartState) : [];
  }

  function ensurePassportListModalStyles() {
    if (document.querySelector('[data-bc-passport-list-modal-styles]')) return;

    const style = document.createElement('style');
    style.dataset.bcPassportListModalStyles = 'true';
    style.textContent = `
      .bc-passport-list-modal[hidden] {
        display: none !important;
      }

      .bc-passport-list-modal {
        position: fixed;
        inset: 0;
        z-index: 2147483000;
        display: grid;
        place-items: center;
        padding: 1.6rem;
      }

      .bc-passport-list-modal__backdrop {
        position: absolute;
        inset: 0;
        background: rgb(20 40 31 / 0.42);
      }

      .bc-passport-list-modal__panel {
        position: relative;
        width: min(48rem, 100%);
        max-height: calc(100dvh - 4rem);
        overflow: auto;
        padding: clamp(2.8rem, 5vw, 4rem);
        color: var(--color-foreground);
        background: var(--color-background);
        border: var(--style-border-width, 1px) solid rgb(var(--color-border-rgb, 0 0 0) / 0.16);
        border-radius: 4px;
        box-shadow: 0 1.6rem 4.8rem rgb(0 0 0 / 0.22);
      }

      .bc-passport-list-modal__title {
        margin: 0 0 2.8rem;
        font-family: var(--font-heading--family);
        font-style: var(--font-heading--style);
        font-weight: var(--font-heading--weight);
        font-size: clamp(3rem, 5vw, 4rem);
        line-height: 1.05;
        color: var(--color-foreground-heading, var(--color-foreground));
      }

      .bc-passport-list-modal__status {
        margin-block: 0 1.6rem;
      }

      .bc-passport-list-modal__status--error {
        color: var(--color-error, #b00020);
      }

      .bc-passport-list-modal__lists {
        display: grid;
        gap: 1.2rem;
      }

      .bc-passport-list-modal__list-button {
        width: 100%;
        padding: 1.6rem 1.8rem;
        text-align: left;
        font: inherit;
        color: inherit;
        background: transparent;
        border: 1px solid rgb(var(--color-border-rgb, 0 0 0) / 0.18);
        cursor: pointer;
      }

      .bc-passport-list-modal__list-button:is(:hover, :focus-visible) {
        border-color: currentColor;
        outline: none;
      }

      .bc-passport-list-modal__form {
        margin-block-start: 2.4rem;
      }

      .bc-passport-list-modal__label {
        display: block;
        margin-block-end: 1.2rem;
        font-size: 1.3rem;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        opacity: 0.7;
      }

      .bc-passport-list-modal__create {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 1.2rem;
      }

      .bc-passport-list-modal__create input {
        min-width: 0;
        padding: 1.4rem 1.6rem;
        color: inherit;
        background: transparent;
        border: 1px solid rgb(var(--color-border-rgb, 0 0 0) / 0.18);
        font: inherit;
      }

      .bc-passport-list-modal__create-button,
      .bc-passport-list-modal__cancel {
        min-height: 4.4rem;
        padding-inline: 2rem;
        text-transform: uppercase;
        letter-spacing: 0.12em;
      }

      .bc-passport-list-modal__cancel {
        margin-block-start: 2.4rem;
      }

      @media screen and (max-width: 749px) {
        .bc-passport-list-modal {
          align-items: end;
          padding: 1rem;
        }

        .bc-passport-list-modal__panel {
          width: 100%;
          max-height: calc(100dvh - 2rem);
          padding: 2.4rem 2rem;
        }

        .bc-passport-list-modal__create {
          grid-template-columns: 1fr;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function ensurePassportListModal() {
    let modal = document.querySelector('[data-bc-passport-list-modal]');
    if (modal) return modal;

    ensurePassportListModalStyles();

    modal = document.createElement('div');
    modal.className = 'bc-passport-list-modal';
    modal.dataset.bcPassportListModal = 'true';
    modal.hidden = true;
    modal.innerHTML = `
      <div class="bc-passport-list-modal__backdrop" data-bc-passport-modal-close></div>
      <div class="bc-passport-list-modal__panel" role="dialog" aria-modal="true" aria-labelledby="bc-passport-list-title">
        <h2 id="bc-passport-list-title" class="bc-passport-list-modal__title">Save to which list?</h2>
        <div class="bc-passport-list-modal__status" data-bc-passport-modal-status hidden></div>
        <div class="bc-passport-list-modal__lists" data-bc-passport-modal-lists></div>
        <form class="bc-passport-list-modal__form" data-bc-passport-modal-form>
          <label class="bc-passport-list-modal__label" for="bc-passport-list-name">OR START A NEW LIST</label>
          <div class="bc-passport-list-modal__create">
            <input id="bc-passport-list-name" type="text" placeholder="e.g. Buy again" autocomplete="off" data-bc-passport-modal-input>
            <button class="button bc-passport-list-modal__create-button" type="submit">Create</button>
          </div>
        </form>
        <button class="button bc-passport-list-modal__cancel" type="button" data-bc-passport-modal-close>Cancel</button>
      </div>
    `;

    document.body.appendChild(modal);

    modal.addEventListener('click', (event) => {
      if (event.target instanceof HTMLElement && event.target.matches('[data-bc-passport-modal-close]')) {
        closePassportListModal();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.hidden) {
        closePassportListModal();
      }
    });

    return modal;
  }

  function setPassportModalStatus(modal, message, isError = false) {
    const status = modal.querySelector('[data-bc-passport-modal-status]');
    if (!(status instanceof HTMLElement)) return;

    status.textContent = message;
    status.hidden = !message;
    status.classList.toggle('bc-passport-list-modal__status--error', isError);
  }

  function closePassportListModal() {
    const modal = document.querySelector('[data-bc-passport-list-modal]');
    if (!modal) return;

    modal.hidden = true;
    document.documentElement.classList.remove('bc-passport-list-modal-open');
  }

  function renderPassportListOptions(modal, btn, productId, lists) {
    const listContainer = modal.querySelector('[data-bc-passport-modal-lists]');
    const form = modal.querySelector('[data-bc-passport-modal-form]');
    const input = modal.querySelector('[data-bc-passport-modal-input]');

    if (!(listContainer instanceof HTMLElement) || !(form instanceof HTMLFormElement) || !(input instanceof HTMLInputElement)) {
      return;
    }

    listContainer.innerHTML = '';

    if (!lists.length) {
      setPassportModalStatus(modal, 'No lists found. Create a new list below.');
    } else {
      setPassportModalStatus(modal, '');
    }

    lists.forEach((list) => {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'bc-passport-list-modal__list-button';
      option.textContent = listLabel(list);
      option.addEventListener('click', async () => {
        option.disabled = true;
        setPassportModalStatus(modal, 'Saving...');

        try {
          const saved = await saveProductToList(btn, productId, list);
          if (saved) {
            showHeartToast(btn, 'Saved to your list!', 'saved');
            closePassportListModal();
          }
        } catch (error) {
          console.error('Passport list save failed', error);
          setPassportModalStatus(modal, 'Could not save to this list.', true);
        } finally {
          option.disabled = false;
        }
      });
      listContainer.appendChild(option);
    });

    form.onsubmit = async (event) => {
      event.preventDefault();

      const name = input.value.trim();
      if (!name) {
        setPassportModalStatus(modal, 'Enter a list name.', true);
        return;
      }

      const submit = form.querySelector('button[type="submit"]');
      if (submit instanceof HTMLButtonElement) submit.disabled = true;
      setPassportModalStatus(modal, 'Creating...');

      try {
        const createdResponse = await createPassportList(name);
        const createdList = createdResponse?.list || createdResponse;
        let listToUse = createdList && listIdentifier(createdList) ? createdList : null;

        if (!listToUse) {
          const refreshedLists = await getPassportLists();
          listToUse = refreshedLists.find((list) => listLabel(list).toLowerCase() === name.toLowerCase());
        }

        if (!listToUse) throw new Error('Created list could not be found.');

        const saved = await saveProductToList(btn, productId, listToUse);
        if (saved) {
          input.value = '';
          showHeartToast(btn, 'Saved to your list!', 'saved');
          closePassportListModal();
        }
      } catch (error) {
        console.error('Passport list create failed', error);
        setPassportModalStatus(modal, 'Could not create this list.', true);
      } finally {
        if (submit instanceof HTMLButtonElement) submit.disabled = false;
      }
    };
  }

  async function openPassportListModal(btn, productId) {
    if (!CFG.customerId) {
      redirectToLogin();
      return;
    }

    const modal = ensurePassportListModal();
    modal.hidden = false;
    document.documentElement.classList.add('bc-passport-list-modal-open');
    setPassportModalStatus(modal, 'Loading...');

    const listContainer = modal.querySelector('[data-bc-passport-modal-lists]');
    if (listContainer instanceof HTMLElement) listContainer.innerHTML = '';

    try {
      const lists = await getPassportLists();
      renderPassportListOptions(modal, btn, productId, lists);
      const firstList = modal.querySelector('.bc-passport-list-modal__list-button, [data-bc-passport-modal-input]');
      if (firstList instanceof HTMLElement) firstList.focus();
    } catch (error) {
      console.error('Passport lists load failed', error);
      if (error.status === 401) {
        redirectToLogin();
        return;
      }
      setPassportModalStatus(modal, 'Could not load your lists.', true);
    }
  }

  async function saveProductToDefaultList(btn, productId) {
    if (!CFG.customerId) {
      redirectToLogin();
      return false;
    }

    try {
      const payload = {
        productId: String(productId),
        variantId: btn.dataset.variantId || '',
        handle: btn.dataset.handle || '',
      };
      await api.post('/customer/lists/default/items', payload);
      localSave(productId, true);
      rememberServerProductState(productId, true);
      setHeartState(btn, true);
      return true;
    } catch (error) {
      localSave(productId, false);
      setHeartState(btn, false);
      if (error.status === 401) {
        redirectToLogin();
        return false;
      }
      throw error;
    }
  }

  async function removeProductFromList(btn, productId) {
    localSave(productId, false);

    try {
      if (CFG.customerId) {
        await api.post(`/customer/lists/default/items/${encodeURIComponent(productId)}/delete`, {});
      }
      setHeartState(btn, false);
      rememberServerProductState(productId, false);
      return true;
    } catch (error) {
      localSave(productId, true);
      setHeartState(btn, true);
      if (error.status === 401) {
        redirectToLogin();
        return false;
      }
      throw error;
    }
  }

  async function toggleHeart(btn, productId) {
    if (btn.disabled) return;

    if (isQuickCardHeartButton(btn)) {
      btn.dataset.bcHeartUserTouched = 'true';
      animateQuickCardHeart(btn);

      if (!clickCardPassportAppButton(btn)) {
        console.warn('Passport app save button was not found for this product card.');
      }

      window.setTimeout(() => {
        btn.dataset.bcHeartUserTouched = 'false';
        syncHeartStateFromServer(btn, productId);
      }, 1200);

      return;
    }

    const saved = btn.classList.contains('bc-heart-btn--active');
    btn.disabled = true;
    btn.dataset.bcHeartUserTouched = 'true';
    btn.classList.remove('bc-heart-btn--clicked');
    void btn.offsetWidth;
    btn.classList.add('bc-heart-btn--clicked');

    try {
      let updated = false;
      if (saved) {
        updated = await removeProductFromList(btn, productId);
      } else {
        updated = await saveProductToDefaultList(btn, productId);
      }

      if (updated) {
        showHeartToast(btn, saved ? 'Removed from your list.' : 'Saved to your list!', saved ? 'removed' : 'saved');
      }
    } finally {
      btn.disabled = false;
    }
  }

  function initHeartButtons(root) {
    const buttonsToSync = [];

    root.querySelectorAll('.bc-heart-btn').forEach((btn) => {
      if (!(btn instanceof HTMLButtonElement)) return;
      if (isPassportAppHeartButton(btn)) return;
      if (!isQuickCardWishlistEnabled(btn)) {
        btn.hidden = true;
        btn.disabled = true;
        return;
      }
      if (btn.dataset.bcHeartInitialized === 'true') return;

      const productId = btn.dataset.productId;
      if (!productId) return;

      btn.dataset.bcHeartInitialized = 'true';
      setHeartState(btn, isProductSaved(productId));
      buttonsToSync.push({ btn, productId });
      btn.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();
        try {
          await toggleHeart(btn, productId);
        } catch (error) {
          console.error('Passport heart update failed', error);
          showHeartToast(btn, 'Could not update your list.', 'error');
        }
      });
    });

    syncHeartButtonsFromServer(buttonsToSync);
  }

  function handleDelegatedQuickHeartClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const btn = target.closest('.quick-add__button--passport.bc-heart-btn');
    if (!(btn instanceof HTMLButtonElement)) return;
    if (!isQuickCardWishlistEnabled(btn)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const productId = btn.dataset.productId;
    if (!productId) return;

    btn.dataset.bcHeartUserTouched = 'true';
    animateQuickCardHeart(btn);

    if (!clickCardPassportAppButton(btn)) {
      console.warn('Passport app save button was not found for this product card.');
    }

    window.setTimeout(() => {
      btn.dataset.bcHeartUserTouched = 'false';
      syncHeartStateFromServer(btn, productId);
    }, 1200);
  }

  function boot() {
    ensureQuickHeartAnimationStyles();
    mountPassportAppHearts(document);
    initHeartButtons(document);
    document.addEventListener('click', handleSaveLabelClick);

    let mountFrame = null;
    const pendingHeartMountScopes = new Set();

    function queueHeartMount(scope) {
      pendingHeartMountScopes.add(scope || document);
      if (mountFrame) return;

      mountFrame = window.requestAnimationFrame(() => {
        mountFrame = null;
        pendingHeartMountScopes.forEach((pendingScope) => mountPassportAppHearts(pendingScope));
        pendingHeartMountScopes.clear();
      });
    }

    if (heartMutationObserver) {
      heartMutationObserver.disconnect();
    }

    heartMutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;

          if (
            node.matches?.('[data-bc-passport-heart-slot], [data-bc-heart-wrap]') ||
            node.querySelector?.('[data-bc-passport-heart-slot], [data-bc-heart-wrap]')
          ) {
            queueHeartMount(heartMountScope(node));
          }

          if (node.matches?.('.bc-heart-btn')) {
            initHeartButtons(node.parentElement || document);
            return;
          }

          if (node.querySelector?.('.bc-heart-btn')) {
            initHeartButtons(node);
          }
        });
      });
    });

    heartMutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
